
import { useState } from "react";
import { useCurrentRentalPoint } from "@/stores/useCurrentRentalPoint";
import { useLogger } from "@/utils/logging/logger";

export type RentalFormData = {
  clientId: string;
  rentalPointId: string;
  returnPointId: string;
  startDate: Date | null;
  endDate: Date | null;
  status: string;
  notes: string;
  discount: number;
  depositAmount: number;
  promoCode: string;
  tariffId: string;
  paymentMethodId: string;
  paymentStatus: string;
  inventoryItems: InventorySelection[];
}

type InventorySelection = {
  id: string;
  quantity: number;
};

type UseRentalFormStateReturn = {
  formData: RentalFormData;
  handleChange: <K extends keyof RentalFormData>(field: K, value: RentalFormData[K]) => void;
  resetForm: () => void;
};

export function useRentalFormState(): UseRentalFormStateReturn {
  const { currentRentalPoint } = useCurrentRentalPoint();
  const logger = useLogger('useRentalFormState');
  
  const [formData, setFormData] = useState<RentalFormData>({
    clientId: '',
    rentalPointId: currentRentalPoint?.id || '',
    returnPointId: '',
    startDate: null,
    endDate: null,
    status: 'pending',
    notes: '',
    discount: 0,
    depositAmount: 0,
    promoCode: '',
    tariffId: '',
    paymentMethodId: '',
    paymentStatus: '',
    inventoryItems: []
  });

  const handleChange = <K extends keyof RentalFormData>(field: K, value: RentalFormData[K]) => {
    logger.debug('Field change', { field: String(field), value });
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      rentalPointId: currentRentalPoint?.id || '',
      returnPointId: '',
      startDate: null,
      endDate: null,
      status: 'pending',
      notes: '',
      discount: 0,
      depositAmount: 0,
      promoCode: '',
      tariffId: '',
      paymentMethodId: '',
      paymentStatus: '',
      inventoryItems: []
    });
  };

  return {
    formData,
    handleChange,
    resetForm
  };
}
